import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxCustomAddress, OnyxIconName } from '@onyx/angular';
import {
  OnyxAddressComponent,
  OnyxDatePipe,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxInformationHeadingComponent,
  OnyxLinkComponent,
  OnyxTimePipe,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { isEqual, omit } from 'lodash';
import type { DelegationStatus } from '../../enums/delegation-status';
import { DelegationHelper } from '../../helpers/delegation.helper';
import type { Delegation } from '../../interfaces/delegation';
import type { RelatedFleetSwap } from './../../../../../common/interfaces/common/related-fleet-swap';

@Component({
  selector: 'app-delegation-period',
  imports: [
    OnyxIconComponent,
    OnyxDatePipe,
    OnyxTimePipe,
    OnyxAddressComponent,
    OnyxLinkComponent,
    TranslatePipe,
    NgTemplateOutlet,
    OnyxInformationHeadingComponent,
    OnyxIconButtonComponent,
    OnyxTooltipDirective,
  ],
  templateUrl: './delegation-period.component.html',
  styleUrl: './delegation-period.component.scss',
})
export class DelegationPeriodComponent {
  public readonly start = input.required<Delegation['start']>();
  public readonly end = input.required<Delegation['end']>();
  public readonly status = input.required<DelegationStatus>();
  public readonly clickable = input.required<boolean>();
  public readonly driverHomeAddress = input<OnyxCustomAddress>();
  public readonly driverAssignedBaseAddress = input<OnyxCustomAddress | null>();
  public readonly heading = input<string>();
  public readonly identifier = input<string>();
  public readonly relatedFleetSwap = input<RelatedFleetSwap | null>();

  public readonly edit = output<void>();
  public readonly openDelegationModal = output<void>();
  public readonly openDelegationActionModal = output<void>();

  protected readonly I18N = 'delegations';

  protected isActive = computed(() => DelegationHelper.isActive(this.status()));
  protected hasEdit = computed(() => DelegationHelper.hasEdit(this.status()));
  protected isPlanned = computed(() =>
    DelegationHelper.isPlanned(this.status()),
  );
  protected isFinished = computed(() =>
    DelegationHelper.isFinished(this.status()),
  );
  protected isCanceled = computed(() =>
    DelegationHelper.isCanceled(this.status()),
  );

  protected getIconName(address: OnyxCustomAddress | null): OnyxIconName {
    const driverHomeAddress = this.driverHomeAddress();
    if (!driverHomeAddress) return 'location-filled';

    const getOmittedAddress = (address: OnyxCustomAddress) =>
      omit(address, 'label');

    const home = getOmittedAddress(driverHomeAddress);
    const base = this.driverAssignedBaseAddress()
      ? getOmittedAddress(this.driverAssignedBaseAddress()!)
      : null;

    const checkAddress = (address: OnyxCustomAddress | undefined | null) => {
      if (!address) return null;

      const omittedAddress = getOmittedAddress(address);
      if (isEqual(omittedAddress, home)) return 'home-filled';
      if (isEqual(omittedAddress, base)) return 'base-filled';

      return null;
    };

    return checkAddress(address) ?? 'location-filled';
  }
}
